import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, fmtDate } from "@/lib/api";
import { House, Plus, MagnifyingGlass, Wrench } from "@phosphor-icons/react";
import HelpLink from "@/components/HelpLink";

/**
 * Home Components · Operational Vault — Part I
 *
 * After v3.3 unification, home components are simply assets where
 * `category=home_component`. This page is a focused view over that subset
 * that surfaces the operational fields (install date, warranty, paint code,
 * specs, service history) inline. All record-new flows go through the
 * regular AddAsset page (Manual / Ask Leo / Voice / Photo OCR / Bulk) with
 * the category pre-set to home_component.
 */
export default function HomeComponents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/assets", { params: { category: "home_component", ...(q ? { q } : {}) } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div className="space-y-6" data-testid="home-components-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pb-6 border-b-2 border-[#C5A059]">
        <div>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] flex items-center gap-3">
            <span>Operational Vault · Part I</span>
            <HelpLink anchor="organised" label="Home Components" />
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0A192F] leading-none mt-2">
            Home Components
          </h1>
          <p className="font-serif-display italic text-sm text-[#5C5F58] mt-3 max-w-xl">
            Roofs, HVAC, paint codes, water heaters, fixtures — the durable
            anatomy of the house, with install dates, warranties, and service
            history kept where any heir or contractor can find them.
          </p>
        </div>
        <div className="flex items-stretch gap-2">
          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F58] pointer-events-none"
            />
            <input
              type="search"
              placeholder="Search components"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="home-components-search"
              className="pl-9 pr-3 py-2 text-sm bg-white border border-[#E0DDD6] w-56 focus:outline-none focus:border-[#1A3622]"
            />
          </div>
          <Link
            to="/assets/new?category=home_component"
            data-testid="home-components-record-new"
            className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-2 text-sm border border-[#0F2818] hover:bg-[#0F2818]"
          >
            <Plus size={14} weight="bold" /> Record New
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#5C5F58] font-mono-ledger text-xs uppercase tracking-[0.25em]">
          Loading the operational record…
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#E0DDD6] bg-white" data-testid="home-components-list">
          {items.map((it) => (
            <ComponentCard key={it.id} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function ComponentCard({ item }) {
  const thumb = item.featured_photo_path || (item.photo_paths || [])[0];
  return (
    <Link
      to={`/assets/${item.id}`}
      data-testid={`home-component-card-${item.id}`}
      className="p-5 border-b border-r border-[#E0DDD6] hover:bg-[#F9F8F6]/60 transition-colors flex gap-4"
    >
      {thumb ? (
        <img
          src={`${API}/files/${thumb}`}
          alt=""
          loading="lazy"
          className="w-20 h-20 object-cover border border-[#E0DDD6] shrink-0"
        />
      ) : (
        <div className="w-20 h-20 bg-[#F9F8F6] border border-[#E0DDD6] flex items-center justify-center shrink-0">
          <House size={22} className="text-[#C9C4B8]" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">
          {(item.component_type || "component").replace(/_/g, " ")}
        </div>
        <div className="font-serif-display text-xl text-[#0A192F] leading-tight truncate">
          {item.name}
        </div>
        {(item.room || item.location) && (
          <div className="text-xs text-[#5C5F58] mt-1">
            {item.room || item.location}
          </div>
        )}
        <div className="font-mono-ledger text-[10px] text-[#5C5F58] mt-2 space-y-0.5">
          {item.install_date && (
            <div>Installed · {fmtDate(item.install_date)}</div>
          )}
          {item.warranty_expiry && (
            <div>Warranty · {fmtDate(item.warranty_expiry)}</div>
          )}
          {item.paint_code && <div className="tabular-nums">Code · {item.paint_code}</div>}
        </div>
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="border border-[#E0DDD6] bg-white p-12 text-center" data-testid="home-components-empty">
      <div className="w-12 h-12 bg-[#F9F8F6] border border-[#E0DDD6] flex items-center justify-center mx-auto mb-4">
        <Wrench size={20} className="text-[#1A3622]" />
      </div>
      <h2 className="font-serif-display text-2xl text-[#0A192F] mb-2">
        The operational vault is empty.
      </h2>
      <p className="text-sm text-[#5C5F58] max-w-md mx-auto mb-6">
        Use the same record-new flows as the rest of the ledger — Manual
        Entry, Ask Leo from a photo, Voice Narration, Photo OCR for model
        plates, or Bulk Document. Category will be set to &quot;Home Component&quot;.
      </p>
      <Link
        to="/assets/new?category=home_component"
        data-testid="home-components-empty-cta"
        className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm border border-[#0F2818] hover:bg-[#0F2818]"
      >
        <Plus size={14} weight="bold" /> Record the first component
      </Link>
    </div>
  );
}
